import { useQuery } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

interface MaintenanceStatus {
  enabled: boolean;
  title?: string;
  message?: string;
  estimated_end?: string | null;
  bypass?: boolean;
}

const REMEMBERED_KEY = 'maintenance-status';

/**
 * What this browser last heard from the server. Shown while the fresh answer
 * is on its way, so a returning visitor to a site in maintenance sees the
 * maintenance page at once instead of a flash of the storefront.
 */
function remembered(): MaintenanceStatus | undefined {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    const value = raw ? (JSON.parse(raw) as MaintenanceStatus) : undefined;
    return value && typeof value.enabled === 'boolean' ? value : undefined;
  } catch {
    return undefined;
  }
}

async function fetchMaintenance(): Promise<MaintenanceStatus> {
  const res = await fetch(`${API_URL}/settings/maintenance-status`, {
    credentials: 'include',
  });
  if (!res.ok) return { enabled: false };
  const status = (await res.json()) as MaintenanceStatus;
  try {
    // `bypass` is about this request's cookie, not the site; never remember it.
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify({ ...status, bypass: undefined }));
  } catch {
    // Storage can be unavailable (private window, blocked site data).
  }
  return status;
}

export function useMaintenanceMode() {
  return useQuery({
    queryKey: ['maintenance-status'],
    queryFn: fetchMaintenance,
    staleTime: 0,
    gcTime: 0,
    placeholderData: remembered,
    refetchInterval: 1000 * 60,
  });
}
